import React, { useState, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  IconButton,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Switch,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import {
  Add,
  Print,
  Close as CloseIcon,
  Edit,
} from "@mui/icons-material";
import { product } from "../../../api/paquetes";

interface PassengerPaymentDialogProps {
  open: boolean;
  onClose: () => void;
  pasajero: any;
  producto: any;
  pagos: any[];
  montoSena: number;
  onAddPago: (pasajero: any, pagoData: { importe: number; observacion: string; moneda: string; fecha?: string }) => Promise<void>;
  onUpdatePago: (pagoId: number, pagoData: { importe: number; observacion: string; fecha?: string }) => Promise<void>;
  onDeletePago: (id: number) => Promise<void>;
  onToggleRevisado?: (id: number, checked: boolean, fecha?: string) => Promise<void>;
  onUpdatePassenger: (pasajero: any) => Promise<void>;
}

export const PassengerPaymentDialog: React.FC<PassengerPaymentDialogProps> = ({
  open,
  onClose,
  pasajero,
  producto,
  pagos = [],
  montoSena,
  onAddPago,
  onUpdatePago,
  onDeletePago,
  onToggleRevisado,
  onUpdatePassenger,
}) => {
  const [modalPagoAbierto, setModalPagoAbierto] = useState(false);
  const [pagoAEditar, setPagoAEditar] = useState<any>(null);
  const [tabIndex, setTabIndex] = useState(0);
  const [pagoData, setPagoData] = useState({
    monto: "",
    condicion: "Seleccione una opción",
    concepto: "EFECTIVO",
    fecha: dayjs() as Dayjs | null,
    percepcion: "",
    tipoCambio: "1420",
  });
  const [observacionData, setObservacionData] = useState(pasajero?.observaciones || "");

  // Actualizar observación cuando cambia el pasajero
  React.useEffect(() => {
    setObservacionData(pasajero?.observaciones || "");
  }, [pasajero]);

  const pagosPasajero = useMemo(() => {
    if (!pasajero) return [];
    // Filtrar pagos que coincidan con el pasajero (por DNI en la observación)
    return pagos.filter((p: any) => 
      p.observacion?.includes(`[PASAJERO:${pasajero.dni}]`) &&
      !p.observacion.toUpperCase().includes("SEÑA")
    );
  }, [pagos, pasajero]);

  const resumen = useMemo(() => {
    const pagado = pagosPasajero.reduce((acc: number, p: any) => acc + (p.importe || 0), 0);
    // El monto total a pagar es el precio de venta del producto.
    // El usuario indicó que la seña se cobra aparte y no descuenta este total.
    const montoTotal = producto?.precioVenta || 0;
    return {
      pagado,
      monto: montoTotal,
      saldo: Math.max(0, montoTotal - pagado),
    };
  }, [pagosPasajero, producto]);

  const handleAbrirModalPago = (pago?: any) => {
    if (pago) {
      setPagoAEditar(pago);
      // Extraer condición del observacion si existe formato [CONDICION: xxx]
      const obs = pago.observacion || "";
      const matchCond = obs.match(/\[CONDICION:\s*(.*?)\]/);
      const condicion = matchCond ? matchCond[1] : "Seleccione una opción";
      const cleanConcepto = obs.replace(/\[CONDICION:.*?\]/g, "").replace(/\[PASAJERO:.*?\]/g, "").trim();

      setPagoData({
        monto: pago.importe.toString(),
        condicion: condicion,
        concepto: obs.replace(/\[.*?\]/g, "").trim(),
        fecha: dayjs(pago.created_at || new Date()),
        percepcion: obs.match(/\[PERV:\s*(.*?)\]/)?.[1] || "",
        tipoCambio: obs.match(/\[TC:\s*(.*?)\]/)?.[1] || "1420",
      });
    } else {
      setPagoAEditar(null);
      setPagoData({
        monto: "",
        condicion: "Seleccione una opción",
        concepto: "",
        fecha: dayjs(),
        percepcion: "",
        tipoCambio: "1420",
      });
    }
    setModalPagoAbierto(true);
  };

  const showExtra = useMemo(() => {
    const cat = (producto?.categoria || "").toLowerCase();
    return ["quince", "educativos", "otros"].includes(cat);
  }, [producto]);

  const handleGuardarPagoInterno = async () => {
    if (!pasajero) return;
    const monto = parseFloat(pagoData.monto);
    if (isNaN(monto)) return;

    if (!pagoData.condicion || pagoData.condicion === "Seleccione una opción") {
      alert("Debe seleccionar una condición de pago");
      return;
    }

    let observacion = `[CONDICION: ${pagoData.condicion}] ${pagoData.concepto} [PASAJERO:${pasajero.dni}]`;
    if (pagoData.percepcion) observacion += ` [PERV:${pagoData.percepcion}]`;
    if (pagoData.tipoCambio) observacion += ` [TC:${pagoData.tipoCambio}]`;

    const data = {
      importe: monto,
      observacion: observacion,
      fecha: pagoData.fecha?.toISOString(),
    };

    if (pagoAEditar) {
      await onUpdatePago(pagoAEditar.id, data);
    } else {
      await onAddPago(pasajero, { ...data, moneda: "ARS" });
    }
    setModalPagoAbierto(false);
  };

  const handleGuardarObservacion = async () => {
    if (!pasajero || !producto) return;
    try {
      await product.updatePasajero(producto.id, pasajero.id, {
        nombre: pasajero.nombre,
        dni: pasajero.dni,
        colegio: pasajero.colegio || "",
        viaja: pasajero.viaja ? 1 : 0,
        observaciones: observacionData
      });
      await onUpdatePassenger({ ...pasajero, observaciones: observacionData });
    } catch (error) {
      console.error("Error al guardar observación del pasajero", error);
    }
  };

  const handleToggleRevisadoInterno = async (pagoId: number, checked: boolean, fechaPago?: string) => {
    if (!onToggleRevisado) return;
    const fecha = checked ? (fechaPago ? dayjs(fechaPago).format("DD/MM/YYYY") : dayjs().format("DD/MM/YYYY")) : "";
    await onToggleRevisado(pagoId, checked, fecha);
  };

  const formatearMoneda = (val: number) => {
    // Analiza la moneda del producto para poner el signo correcto
    const isUSD = (producto?.moneda || "").toUpperCase() === "USD";
    const signo = isUSD ? "U$D" : "$";
    return `${signo} ${val.toLocaleString("es-AR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  if (!pasajero) return null;
  
  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Dialog 
        open={open} 
        onClose={onClose} 
        maxWidth="lg" 
        fullWidth
        PaperProps={{ sx: { borderRadius: 1 } }}
      >
        <DialogTitle sx={{ m: 0, p: 2, display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: "1px solid #eee" }}>
          <Typography sx={{ color: "#666", fontSize: "1.1rem" }}>
            Pagos | <span style={{ fontWeight: "bold", color: "#461264" }}>{(pasajero.nombre || "").toUpperCase()}</span> | {producto?.concepto || "PAQUETE"}
          </Typography>
          <Box sx={{ display: "flex", gap: 1 }}>
              <Button 
                variant="contained" 
                startIcon={<Add />} 
                size="small" 
                sx={{ bgcolor: "#461264", textTransform: "none", "&:hover": { bgcolor: "#340d4a" } }}
                onClick={() => handleAbrirModalPago()}
              >
                Agregar Nuevo Pago
              </Button>
            <Button 
              variant="contained" 
              startIcon={<Print />} 
              size="small" 
              sx={{ bgcolor: "#55c2d5", textTransform: "none" }}
            >
              Imprimir Ficha
            </Button>
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 2 }}>
          {/* TABS */}
          <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
             <Button 
               sx={{ 
                 color: tabIndex === 0 ? "#461264" : "#999", 
                 borderBottom: tabIndex === 0 ? "2px solid #461264" : "none", 
                 borderRadius: 0, px: 4, textTransform: "none", fontWeight: tabIndex === 0 ? "bold" : "normal"
               }}
               onClick={() => setTabIndex(0)}
             >
               Pagos
             </Button>
             <Button 
               sx={{ 
                 color: tabIndex === 1 ? "#461264" : "#999", 
                 borderBottom: tabIndex === 1 ? "2px solid #461264" : "none", 
                 borderRadius: 0, px: 4, textTransform: "none", fontWeight: tabIndex === 1 ? "bold" : "normal"
               }}
               onClick={() => setTabIndex(1)}
             >
               Observaciones
             </Button>
          </Box>


          {tabIndex === 0 && (
          <Box>
            {pagosPasajero.length === 0 ? (
              <Box sx={{ mt: 2 }}>
                <Typography variant="body1" sx={{ color: "#999", mb: 3 }}>
                  No hay pagos registrados para el pasajero hasta el momento.
                </Typography>
                <Box sx={{ width: 300 }}>
                    <Box sx={{ p: 1, textAlign: "center", border: "1px solid #eee", borderBottom: "none", fontWeight: "bold", color: "#666" }}>
                        Total a pagar
                    </Box>
                    <Box sx={{ p: 1, textAlign: "center", bgcolor: "#f3e5f5", fontWeight: "bold", border: "1px solid #eee", fontSize: "1.1rem" }}>
                        {formatearMoneda(resumen.monto)}
                    </Box>
                </Box>
              </Box>
            ) : (
              <Box>
                <TableContainer component={Paper} elevation={0} sx={{ border: "1px solid #eee" }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: "#f9f9f9" }}>
                        <TableCell sx={{ fontWeight: "bold", textAlign: "center" }}>Acciones</TableCell>
                        <TableCell sx={{ fontWeight: "bold" }}>#</TableCell>
                        <TableCell sx={{ fontWeight: "bold" }}>Condición</TableCell>
                        <TableCell sx={{ fontWeight: "bold" }}>Entregado</TableCell>
                        {showExtra && (
                          <>
                            <TableCell sx={{ fontWeight: "bold" }}>Percepción</TableCell>
                            <TableCell sx={{ fontWeight: "bold" }}>Tipo Cambio</TableCell>
                            <TableCell sx={{ fontWeight: "bold" }}>Importe U$D</TableCell>
                          </>
                        )}
                        <TableCell sx={{ fontWeight: "bold" }}>Producto</TableCell>
                        <TableCell sx={{ fontWeight: "bold" }}>Fecha de pago</TableCell>
                        <TableCell sx={{ fontWeight: "bold" }}>Concepto</TableCell>
                        <TableCell sx={{ fontWeight: "bold" }}>Responsable</TableCell>
                        <TableCell sx={{ fontWeight: "bold" }}>Sucursal</TableCell>
                        <TableCell sx={{ fontWeight: "bold", textAlign: "center" }}>Revisado</TableCell>
                        <TableCell sx={{ fontWeight: "bold" }}>Revisado por</TableCell>
                        <TableCell sx={{ fontWeight: "bold" }}>Fecha de revisión</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {pagosPasajero.map((p: any, idx: number) => (
                        <TableRow key={p.id} hover>
                          <TableCell sx={{ textAlign: "center", py: 0.5 }}>
                            <Box sx={{ display: "flex", justifyContent: "center", gap: 0.5 }}>
                              <IconButton size="small" sx={{ color: "#461264", p: 0.5 }} onClick={() => handleAbrirModalPago(p)}><Edit fontSize="inherit" /></IconButton>
                              <IconButton size="small" sx={{ color: "#461264", p: 0.5 }} onClick={() => onDeletePago(p.id)}><CloseIcon fontSize="inherit" /></IconButton>
                            </Box>
                          </TableCell>
                          <TableCell>{idx + 1}</TableCell>
                          <TableCell>{(p.observacion || "").match(/\[CONDICION:\s*(.*?)\]/)?.[1] || "-"}</TableCell>
                          <TableCell>{formatearMoneda(p.importe)}</TableCell>
                          {showExtra && (
                            <>
                              <TableCell>{(p.observacion || "").match(/\[PERV:\s*(.*?)\]/)?.[1] || "-"}</TableCell>
                              <TableCell>{(p.observacion || "").match(/\[TC:\s*(.*?)\]/)?.[1] || "-"}</TableCell>
                              <TableCell>
                                {(() => {
                                  const tc = parseFloat((p.observacion || "").match(/\[TC:\s*(.*?)\]/)?.[1] || "0");
                                  const perv = parseFloat((p.observacion || "").match(/\[PERV:\s*(.*?)\]/)?.[1] || "0");
                                  const totalPesos = (p.importe || 0) + perv;
                                  return tc > 0 ? `U$D ${(totalPesos / tc).toLocaleString("es-AR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : "-";
                                })()}
                              </TableCell>
                            </>
                          )}
                          <TableCell>{formatearMoneda(p.importe)}</TableCell>
                          <TableCell>{p.created_at ? dayjs(p.created_at).format("DD/MM/YYYY") : "-"}</TableCell>
                          <TableCell>{(p.observacion || "").replace(/\[.*?\]/g, "").trim() || "-"}</TableCell>
                          <TableCell>{p.responsable_nombre || "-"}</TableCell>
                          <TableCell>01_ESTUDIANTIL</TableCell>
                          <TableCell sx={{ textAlign: "center" }}>
                            <Box sx={{ 
                              display: "flex", 
                              alignItems: "center", 
                              justifyContent: "center",
                              bgcolor: p.revisado_por_nombre ? "#4caf50" : "#f44336",
                              borderRadius: 1,
                              px: 0.5,
                              width: "fit-content",
                              mx: "auto"
                            }}>
                              <Typography variant="caption" sx={{ color: "white", fontWeight: "bold", mr: 0.5 }}>
                                {p.revisado_por_nombre ? "SI" : "NO"}
                              </Typography>
                              <Switch 
                                size="small" 
                                checked={!!p.revisado_por_nombre}
                                onChange={(e) => handleToggleRevisadoInterno(p.id, e.target.checked, p.created_at)}
                                sx={{ 
                                  "& .MuiSwitch-switchBase.Mui-checked": { color: "white" },
                                  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { bgcolor: "white" },
                                  width: 34, height: 20, p: 0,
                                  "& .MuiSwitch-thumb": { width: 14, height: 14, mt: 0.4, ml: 0.4 }
                                }}
                              />
                            </Box>
                          </TableCell>
                          <TableCell>{p.revisado_por_nombre || ""}</TableCell>
                          <TableCell>{p.fecha_revision || ""}</TableCell>
                        </TableRow>
                      ))}
                      <TableRow sx={{ bgcolor: "#f5f7fa" }}>
                        <TableCell sx={{ fontWeight: "bold", py: 1 }}>Pagado</TableCell>
                        <TableCell colSpan={2}></TableCell>
                        <TableCell sx={{ fontWeight: "bold" }}>{formatearMoneda(resumen.pagado)}</TableCell>
                        {showExtra && <TableCell colSpan={3}></TableCell>}
                        <TableCell sx={{ fontWeight: "bold" }}>{formatearMoneda(resumen.pagado)}</TableCell>
                        <TableCell colSpan={7}></TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>

                {/* CUADROS DE RESUMEN INFERIOR */}
                <Box sx={{ mt: 3, display: "flex", gap: 4 }}>
                  <Box sx={{ width: 300 }}>
                      <Table size="small" sx={{ border: "1px solid #eee" }}>
                        <TableBody>
                          <TableRow>
                            <TableCell sx={{ bgcolor: "#f3e5f5", fontWeight: "bold", width: "40%" }}>Monto</TableCell>
                            <TableCell sx={{ textAlign: "right", fontWeight: "bold" }}>{formatearMoneda(resumen.monto)}</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell sx={{ bgcolor: "#e1bee7", fontWeight: "bold" }}>Pagado</TableCell>
                            <TableCell sx={{ textAlign: "right", fontWeight: "bold", bgcolor: "#e1bee7" }}>{formatearMoneda(resumen.pagado)}</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell sx={{ bgcolor: "#f3e5f5", fontWeight: "bold" }}>Saldo</TableCell>
                            <TableCell sx={{ textAlign: "right", fontWeight: "bold" }}>{formatearMoneda(resumen.saldo)}</TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                  </Box>

                  <Box sx={{ flex: 1, border: "1px solid #eee", p: 1 }}>
                      <Box sx={{ display: "flex", borderBottom: "1px solid #eee", pb: 1, mb: 1 }}>
                        <Typography variant="caption" sx={{ flex: 1, fontWeight: "bold" }}>Fecha</Typography>
                        <Typography variant="caption" sx={{ flex: 1, fontWeight: "bold" }}>Cambio</Typography>
                        <Typography variant="caption" sx={{ flex: 1, fontWeight: "bold" }}>Usuario</Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary" align="center">No hay información para mostrar en la tabla</Typography>
                  </Box>
                </Box>
              </Box>
            )}
          </Box>
          )}

          {tabIndex === 1 && (
             <Box sx={{ p: 4, bgcolor: "#f9f9f9", borderRadius: 1 }}>
                <Typography variant="body2" sx={{ mb: 1, fontWeight: "bold", color: "#666" }}>Observaciones del pasajero</Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={8}
                  value={observacionData}
                  onChange={(e) => setObservacionData(e.target.value)}
                  placeholder="Escriba aquí las observaciones..."
                  sx={{ bgcolor: "white", mb: 2 }}
                />
                <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                  <Button 
                    variant="contained" 
                    onClick={handleGuardarObservacion}
                    startIcon={<Edit />}
                    sx={{ bgcolor: "#461264", "&:hover": { bgcolor: "#340d4a" }, textTransform: "none" }}
                  >
                    Guardar Observaciones
                  </Button>
                </Box>
             </Box>
          )}
        </DialogContent>

        {/* DIALOGO AGREGAR/EDITAR PAGO */}
        <Dialog 
          open={modalPagoAbierto} 
          onClose={() => setModalPagoAbierto(false)} 
          maxWidth="lg" 
          fullWidth
          PaperProps={{ sx: { borderRadius: 1 } }}
        >
          <DialogTitle sx={{ m: 0, p: 2, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Box></Box>
            <Box sx={{ textAlign: "center" }}>
              <Typography variant="h6" sx={{ color: "#666", fontWeight: "bold" }}>{pagoAEditar ? "Editar Pago" : "Agregar Nuevo Pago"}</Typography>
              <Typography variant="caption" sx={{ color: "#999", display: "block" }}>Cambio del día</Typography>
              <Typography variant="caption" sx={{ color: "#999", display: "block" }}>U$S 1420 | €</Typography>
            </Box>
            <IconButton onClick={() => setModalPagoAbierto(false)} size="small">
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ p: 4 }}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
              
              {/* Monto en pesos */}
              <Box>
                <Typography variant="caption" sx={{ display: "block", mb: 0.5, fontWeight: "bold", color: "#666" }}>Monto en pesos</Typography>
                <TextField 
                  fullWidth 
                  size="small"
                  type="number" 
                  value={pagoData.monto} 
                  onChange={(e) => setPagoData({...pagoData, monto: e.target.value})}
                  sx={{ bgcolor: "#fafafa" }}
                />
              </Box>

              {/* Condición de pago */}
              <Box>
                <Typography variant="caption" sx={{ display: "block", mb: 0.5, fontWeight: "bold", color: "#666" }}>Condición de pago</Typography>
                <TextField
                  select
                  fullWidth
                  size="small"
                  value={pagoData.condicion} 
                  onChange={(e) => setPagoData({...pagoData, condicion: e.target.value})} 
                  sx={{ bgcolor: "#fafafa" }}
                  SelectProps={{ native: true }}
                >
                  <option value="Seleccione una opción">Seleccione una opción</option>
                  <option value="Efectivo">Efectivo</option>
                  <option value="Transferencia">Transferencia</option>
                  <option value="Tarjeta de crédito">Tarjeta de crédito</option>
                  <option value="Cheque">Cheque</option>
                </TextField>
              </Box>

              {/* Percepción (Solo si showExtra es true) */}
              {showExtra && (
                <Box>
                  <Typography variant="caption" sx={{ display: "block", mb: 0.5, fontWeight: "bold", color: "#666" }}>Percepción</Typography>
                  <TextField 
                    fullWidth 
                    size="small"
                    type="number"
                    value={pagoData.percepcion} 
                    onChange={(e) => setPagoData({...pagoData, percepcion: e.target.value})}
                    sx={{ bgcolor: "#fafafa" }}
                  />
                </Box>
              )}

              {/* Tipo de cambio */}
              <Box>
                <Typography variant="caption" sx={{ display: "block", mb: 0.5, fontWeight: "bold", color: "#666" }}>Tipo de cambio</Typography>
                <TextField 
                  fullWidth 
                  size="small"
                  type="number"
                  value={pagoData.tipoCambio}
                  onChange={(e) => setPagoData({...pagoData, tipoCambio: e.target.value})}
                  sx={{ bgcolor: showExtra ? "#fafafa" : "#F5F5F5" }}
                  disabled={!showExtra}
                />
              </Box>

              {/* Total en Dólares (Calculado) */}
              {showExtra && (
                <Box>
                  <Typography variant="caption" sx={{ display: "block", mb: 0.5, fontWeight: "bold", color: "#666" }}>Total en Dólares</Typography>
                  <TextField 
                    fullWidth 
                    size="small"
                    value={(() => {
                      const monto = parseFloat(pagoData.monto) || 0;
                      const perv = parseFloat(pagoData.percepcion) || 0;
                      const tc = parseFloat(pagoData.tipoCambio) || 0;
                      if (tc > 0) return `U$D ${((monto + perv) / tc).toLocaleString("es-AR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
                      return "-";
                    })()}
                    disabled
                    sx={{ bgcolor: "#F5F5F5" }}
                  />
                </Box>
              )}

              {/* Monto Total */}
              <Box>
                <Typography variant="caption" sx={{ display: "block", mb: 0.5, fontWeight: "bold", color: "#666" }}>Monto Total</Typography>
                <TextField 
                  fullWidth 
                  size="small"
                  value={pagoData.monto}
                  disabled
                  sx={{ bgcolor: "#F5F5F5" }}
                />
              </Box>

              {/* Fecha de pago */}
              <Box sx={{ display: "flex", alignItems: "center", border: "1px solid #ccc", borderRadius: 1, overflow: "hidden" }}>
                <Box sx={{ bgcolor: "#eee", px: 2, py: 1.5, borderRight: "1px solid #ccc", minWidth: 150 }}>
                  <Typography variant="caption" sx={{ fontWeight: "bold", color: "#666" }}>Fecha de pago</Typography>
                </Box>
                <Box sx={{ flex: 1, px: 2, bgcolor: "#fafafa" }}>
                    <DatePicker 
                    value={pagoData.fecha}
                    onChange={(val) => setPagoData({...pagoData, fecha: val})}
                    slotProps={{ 
                      textField: { 
                        variant: "standard", 
                        fullWidth: true, 
                        InputProps: { disableUnderline: true } 
                      } 
                    }}
                  />
                </Box>
              </Box>

              {/* Concepto */}
              <Box>
                <Typography variant="caption" sx={{ display: "block", mb: 0.5, fontWeight: "bold", color: "#666" }}>Concepto</Typography>
                <TextField 
                  fullWidth 
                  size="small"
                  value={pagoData.concepto} 
                  onChange={(e) => setPagoData({...pagoData, concepto: e.target.value.toUpperCase()})}
                  sx={{ bgcolor: "#fafafa" }}
                />
              </Box>

            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 4, pt: 0, justifyContent: "flex-end" }}>
            <Button 
              variant="contained" 
              onClick={handleGuardarPagoInterno} 
              sx={{ bgcolor: "#66bb6a", "&:hover": { bgcolor: "#43a047" }, color: "white", px: 4, fontWeight: "bold", textTransform: "none" }}
            >
              GUARDAR
            </Button>
          </DialogActions>
        </Dialog>
      </Dialog>
    </LocalizationProvider>
  );
};
